import { ref, watch, onBeforeUnmount, type Ref } from 'vue';

export interface PaginationOptions {
  pageSize?: number;
  rootMargin?: string;
}

export function usePagination<T>(
  items: Ref<T[]>,
  loadMore: () => Promise<void>,
  options: PaginationOptions = {}
) {
  const { pageSize = 20, rootMargin = '200px' } = options;

  const currentPage = ref(1);
  const hasMoreResults = ref(false);
  const isLoadingMore = ref(false);
  const loadMoreObserver = ref<IntersectionObserver | null>(null);
  const sentinelRef = ref<HTMLElement | null>(null);

  const handleLoadMore = async () => {
    if (isLoadingMore.value || !hasMoreResults.value) return;

    isLoadingMore.value = true;
    try {
      await loadMore();
      currentPage.value += 1;
    } finally {
      isLoadingMore.value = false;
    }
  };

  const setupObserver = () => {
    if (loadMoreObserver.value) {
      loadMoreObserver.value.disconnect();
      loadMoreObserver.value = null;
    }

    if (!hasMoreResults.value || !sentinelRef.value) return;
    if (typeof IntersectionObserver === 'undefined') return;

    loadMoreObserver.value = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin }
    );

    loadMoreObserver.value.observe(sentinelRef.value);
  };

  const reset = () => {
    currentPage.value = 1;
    hasMoreResults.value = false;
    isLoadingMore.value = false;
  };

  const updateHasMore = (itemCount: number, meta?: { hasMore?: boolean | null }) => {
    if (meta?.hasMore !== undefined && meta?.hasMore !== null) {
      hasMoreResults.value = meta.hasMore;
    } else {
      hasMoreResults.value = itemCount >= pageSize;
    }
  };

  watch([hasMoreResults, items], () => {
    setupObserver();
  });

  onBeforeUnmount(() => {
    if (loadMoreObserver.value) {
      loadMoreObserver.value.disconnect();
    }
  });

  return {
    currentPage,
    hasMoreResults,
    isLoadingMore,
    sentinelRef,
    reset,
    updateHasMore,
    handleLoadMore,
  };
}
